import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { adminApi } from '@vmgate/common';
import type {
  CreateCustomerPayload,
  CustomerSummary,
  InstallationService,
  SupportContractStatus,
  UpdateCustomerPayload
} from '@vmgate/common';
import { useEffect, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';

const installationOptions = ['NONE', 'REMOTE_INSTALL', 'REMOTE_INSTALL_ROOT', 'CUSTOMER'] as const;
const supportOptions = ['NONE', 'ACTIVE', 'PENDING', 'EXPIRED'] as const;
const statusOptions = ['ACTIVE', 'SUSPENDED', 'INACTIVE'] as const;

const baseSchema = z.object({
  company_name: z.string().min(1, 'Company name is required'),
  contact_email: z.string().email('Valid email required'),
  contact_phone: z.string().optional(),
  address: z.string().optional(),
  notes: z.string().optional(),
  license_quota_total: z.coerce.number().min(0, 'Quota must be >= 0'),
  installation_service: z.enum(installationOptions),
  support_contract_status: z.enum(supportOptions),
  status: z.enum(statusOptions).optional()
});

type CustomerFormValues = z.infer<typeof baseSchema>;

interface CustomerFormResult {
  customer_id: string;
  deployment_token?: string;
}

interface CustomerFormProps {
  mode: 'create' | 'edit';
  customer?: CustomerSummary | null;
  onSuccess: (result: CustomerFormResult | null) => void;
}

export function CustomerForm({ mode, customer, onSuccess }: CustomerFormProps) {
  const isEdit = mode === 'edit' && customer;
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors }
  } = useForm<CustomerFormValues>({
    resolver: zodResolver(baseSchema),
    defaultValues: {
      company_name: customer?.company_name ?? '',
      contact_email: customer?.contact_email ?? '',
      contact_phone: customer?.contact_phone ?? '',
      address: customer?.address ?? '',
      notes: customer?.notes ?? '',
      license_quota_total: customer?.license_quota_total ?? 0,
      installation_service: (customer?.installation_service ?? 'NONE') as InstallationService,
      support_contract_status: (customer?.support_contract_status ?? 'NONE') as SupportContractStatus,
      status: customer?.status ?? 'ACTIVE'
    }
  });

  useEffect(() => {
    reset({
      company_name: customer?.company_name ?? '',
      contact_email: customer?.contact_email ?? '',
      contact_phone: customer?.contact_phone ?? '',
      address: customer?.address ?? '',
      notes: customer?.notes ?? '',
      license_quota_total: customer?.license_quota_total ?? 0,
      installation_service: (customer?.installation_service ?? 'NONE') as InstallationService,
      support_contract_status: (customer?.support_contract_status ?? 'NONE') as SupportContractStatus,
      status: customer?.status ?? 'ACTIVE'
    });
  }, [customer, mode, reset]);

  const assignedQuota = customer?.license_quota_assigned ?? 0;
  const quotaValue = watch('license_quota_total');

  const onSubmit = async (values: CustomerFormValues) => {
    setError(null);
    setSubmitting(true);
    try {
      let result: CustomerFormResult | null = null;
      if (isEdit) {
        const payload: UpdateCustomerPayload = {
          company_name: values.company_name,
          contact_email: values.contact_email,
          contact_phone: values.contact_phone,
          address: values.address,
          notes: values.notes,
          license_quota_total: values.license_quota_total,
          installation_service: values.installation_service,
          support_contract_status: values.support_contract_status,
          status: values.status
        };
        await adminApi.updateCustomer(customer!.customer_id, payload);
        result = {
          customer_id: customer!.customer_id,
          deployment_token: customer!.deployment_token
        };
      } else {
        const payload: CreateCustomerPayload = {
          company_name: values.company_name,
          contact_email: values.contact_email,
          contact_phone: values.contact_phone,
          address: values.address,
          notes: values.notes,
          license_quota_total: values.license_quota_total,
          installation_service: values.installation_service,
          support_contract_status: values.support_contract_status
        };
        const created = await adminApi.createCustomer(payload);
        result = {
          customer_id: created.customer_id,
          deployment_token: created.deployment_token
        };
      }
      await queryClient.invalidateQueries({ queryKey: ['customers'] });
      onSuccess(result);
    } catch (err) {
      console.error(err);
      setError('Failed to save customer. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'grid', gap: '1rem' }}>
        <Field label="Company name" error={errors.company_name?.message}>
          <input
            type="text"
            {...register('company_name')}
            placeholder="Acme Telecom"
            className="surface"
            style={inputStyle}
          />
        </Field>

        <Field label="Contact email" error={errors.contact_email?.message}>
          <input type="email" {...register('contact_email')} placeholder="ops@example.com" className="surface" style={inputStyle} />
        </Field>

        <Field label="Contact phone" error={errors.contact_phone?.message}>
          <input type="tel" {...register('contact_phone')} placeholder="+1 555 1234" className="surface" style={inputStyle} />
        </Field>

        <Field label="Address" error={errors.address?.message}>
          <textarea {...register('address')} rows={2} placeholder="Optional address details" className="surface" style={{ ...inputStyle, minHeight: '70px' }} />
        </Field>

        <Field label="Notes" error={errors.notes?.message}>
          <textarea {...register('notes')} rows={2} placeholder="Internal notes (visible to admin only)" className="surface" style={{ ...inputStyle, minHeight: '70px' }} />
        </Field>

        <Field
          label={`License quota total${assignedQuota ? ` (assigned: ${assignedQuota})` : ''}`}
          error={
            quotaValue < assignedQuota
              ? `Cannot be below assigned licenses (${assignedQuota})`
              : errors.license_quota_total?.message
          }
        >
          <input type="number" min={assignedQuota} {...register('license_quota_total')} className="surface" style={inputStyle} />
        </Field>

        <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
          <Field label="Installation service">
            <select {...register('installation_service')} className="surface" style={inputStyle}>
              {installationOptions.map((option) => (
                <option key={option} value={option}>
                  {option.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Support contract">
            <select {...register('support_contract_status')} className="surface" style={inputStyle}>
              {supportOptions.map((option) => (
                <option key={option} value={option}>
                  {option.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </Field>

          {isEdit && (
            <Field label="Status">
              <select {...register('status')} className="surface" style={inputStyle}>
                {statusOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </Field>
          )}
        </div>
      </div>

      {error && (
        <div
          style={{
            background: 'rgba(220, 38, 38, 0.12)',
            color: '#b91c1c',
            borderRadius: '10px',
            padding: '0.75rem'
          }}
        >
          {error}
        </div>
      )}

      <button type="submit" className="btn btn-primary" disabled={submitting}>
        {submitting ? 'Saving...' : isEdit ? 'Update Customer' : 'Create Customer'}
      </button>
    </form>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
      <span style={{ fontWeight: 600 }}>{label}</span>
      {children}
      {error && <span style={{ color: 'var(--color-danger)', fontSize: '0.85rem' }}>{error}</span>}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  border: '1px solid var(--color-border)',
  borderRadius: '10px',
  padding: '0.65rem 0.85rem',
  fontSize: '0.95rem',
  background: '#fff',
  boxShadow: 'none'
};

